import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  CheckCircle2,
  Code2,
  Eye,
  FileText,
  History,
  Image as ImageIcon,
  Loader2,
  Mail,
  RefreshCw,
  RotateCcw,
  Save,
  Send,
  Settings2,
  Upload,
  XCircle,
} from 'lucide-react';
import { API } from '../../context/AuthContext';
import './AdminEmailCenter.css';

const SAMPLE_VALUES = {
  empresa: 'PROVI',
  company_name: 'PROVI',
  nombre: 'Jean PROVI',
  user_name: 'Jean PROVI',
  codigo: '483921',
  otp_code: '483921',
  anio: new Date().getFullYear(),
  year: new Date().getFullYear(),
  url_app: 'https://proviapp.com',
  url_recuperar: 'https://proviapp.com/reset-password?token=PRUEBA',
  reset_link: 'https://proviapp.com/reset-password?token=PRUEBA',
  fecha_hora: new Date().toLocaleString('es-EC'),
  dispositivo: 'Chrome en Windows',
  ubicacion: 'Santa Rosa, El Oro',
  navegador: 'Google Chrome',
  ip: '192.0.2.10',
  tipo_proveedor: 'Taxista',
  plazo_verificacion: '48',
  url_documentos: 'https://proviapp.com/provider',
  nombre_invitador: 'Superadmin PROVI',
  rol: 'Administrador',
  correo: 'prueba@proviapp.com',
  horas_expiracion: '24',
  url_activacion: 'https://proviapp.com/admin-access',
  tipo_solicitud: 'Administrador',
  request_type: 'Administrador',
  motivo: 'Información incompleta',
  reason: 'Información incompleta',
  comentario: 'Completa la información solicitada y vuelve a intentarlo.',
  comment: 'Completa la información solicitada y vuelve a intentarlo.',
  minutos_expiracion: '15',
  ancho_logo: '120',
  ubicacion_empresa: 'Santa Rosa, El Oro, Ecuador',
  url_terminos: 'https://proviapp.com/terminos',
  url_privacidad: 'https://proviapp.com/privacidad',
  url_soporte: 'https://proviapp.com/soporte',
  correo_privacidad: 'soporte@proviapp.com',
};

const replaceVariables = (content, values) => String(content || '').replace(
  /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g,
  (_, key) => String(values[key] ?? ''),
);


const EMAIL_MAX_SIMPLE_FIELDS = 4;

const EMAIL_TEXT_SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'TITLE',
]);

const EMAIL_PROTECTED_TEXT_PATTERNS = [
  /^PROVI$/i,
  /^MOVILIDAD\b/i,
  /^©/i,
  /^Términos y Condiciones$/i,
  /^Política de Privacidad$/i,
  /^Centro de Ayuda$/i,
  /^PROVI trata tus datos/i,
  /^Puedes ejercer tus derechos/i,
  /^Este es un correo automático/i,
];

const EMAIL_DYNAMIC_DETAIL_PATTERN =
  /fecha|dispositivo|navegador|ubicación|dirección ip|\bip\b/i;

const EMAIL_HEADER_SPACING = {
  compact: {
    top: 16,
    bottom: 12,
  },
  normal: {
    top: 28,
    bottom: 20,
  },
  roomy: {
    top: 38,
    bottom: 28,
  },
};

const getElementPath = (element, root) => {
  const path = [];
  let current = element;

  while (current && current !== root) {
    const parent = current.parentNode;

    if (!parent) {
      return [];
    }

    path.unshift(
      Array.prototype.indexOf.call(
        parent.childNodes,
        current,
      ),
    );

    current = parent;
  }

  return path;
};

const findElementByPath = (
  root,
  path,
) => {
  let current = root;

  for (const index of path || []) {
    current = current?.childNodes?.[index];

    if (!current) {
      return null;
    }
  }

  return current;
};

const getEditableElementText = (element) => {
  if (!element) {
    return '';
  }

  const clone = element.cloneNode(true);

  clone.querySelectorAll('br').forEach(
    (breakNode) => {
      breakNode.replaceWith('\n');
    },
  );

  return String(clone.textContent || '')
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

const getTemplateVariables = (value) => Array.from(
  String(value || '').matchAll(
    /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g,
  ),
).map((match) => match[1]);

const isProtectedEmailElement = (element) => {
  if (!element) {
    return true;
  }

  const text = getEditableElementText(element);

  if (
    EMAIL_PROTECTED_TEXT_PATTERNS.some(
      (pattern) => pattern.test(text),
    )
  ) {
    return true;
  }

  let current = element;

  while (
    current
    && current.tagName !== 'BODY'
  ) {
    const style = String(
      current.getAttribute('style') || '',
    )
      .toLowerCase()
      .replace(/\s+/g, '');

    if (
      style.includes('display:none')
      || style.includes('max-height:0')
      || style.includes(
        'background-color:#0b1026',
      )
      || style.includes(
        'background:#0b1026',
      )
    ) {
      return true;
    }

    current = current.parentElement;
  }

  return false;
};

const isAutomaticDetailParagraph = (
  element,
) => {
  const value = getEditableElementText(
    element,
  );

  const variables = getTemplateVariables(
    value,
  );

  return (
    variables.length >= 2
    && EMAIL_DYNAMIC_DETAIL_PATTERN.test(value)
  );
};

const getSimpleFieldLabel = (
  kind,
  value,
  index,
) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (kind === 'title') {
    return 'Título principal';
  }

  if (kind === 'button') {
    return 'Texto del botón';
  }

  if (
    normalized.startsWith('hola')
  ) {
    return 'Saludo y mensaje principal';
  }

  if (
    normalized.includes('si no reconoces')
    || normalized.includes('importante')
    || normalized.includes('seguridad')
  ) {
    return 'Aviso importante';
  }

  if (
    normalized.includes('gracias')
    || normalized.includes('equipo provi')
  ) {
    return 'Cierre del correo';
  }

  return index === 0
    ? 'Mensaje principal'
    : 'Mensaje adicional';
};

const extractVisibleTextBlocks = (
  htmlContent,
) => {
  if (
    !htmlContent
    || typeof DOMParser === 'undefined'
  ) {
    return [];
  }

  const parser = new DOMParser();

  const documentNode = parser.parseFromString(
    String(htmlContent),
    'text/html',
  );

  const body = documentNode.body;

  const fields = [];

  const title = Array.from(
    body.querySelectorAll('h1, h2, h3'),
  ).find(
    (element) =>
      !isProtectedEmailElement(element),
  );

  if (title) {
    const value = getEditableElementText(
      title,
    );

    fields.push({
      id: 'title',
      path: getElementPath(title, body),
      value,
      variables:
        getTemplateVariables(value),
      kind: 'title',
      label: 'Título principal',
    });
  }

  const paragraphs = Array.from(
    body.querySelectorAll('p'),
  ).filter((element) => {
    const value =
      getEditableElementText(element);

    return (
      value.length >= 8
      && !isProtectedEmailElement(element)
      && !isAutomaticDetailParagraph(element)
    );
  });

  const maximumParagraphs =
    title ? 2 : 3;

  paragraphs
    .slice(0, maximumParagraphs)
    .forEach((element, index) => {
      const value =
        getEditableElementText(element);

      fields.push({
        id: `paragraph-${index}`,
        path: getElementPath(
          element,
          body,
        ),
        value,
        variables:
          getTemplateVariables(value),
        kind: 'paragraph',
        label: getSimpleFieldLabel(
          'paragraph',
          value,
          index,
        ),
      });
    });

  const primaryButton = Array.from(
    body.querySelectorAll('a'),
  ).find((element) => {
    if (
      isProtectedEmailElement(element)
    ) {
      return false;
    }

    const href = String(
      element.getAttribute('href') || '',
    );

    const style = String(
      element.getAttribute('style') || '',
    ).toLowerCase();

    return (
      href.includes('{{url_')
      || style.includes(
        'background-color',
      )
      || style.includes(
        'background-image',
      )
    );
  });

  if (
    primaryButton
    && fields.length
      < EMAIL_MAX_SIMPLE_FIELDS
  ) {
    const value =
      getEditableElementText(
        primaryButton,
      );

    fields.push({
      id: 'primary-button',
      path: getElementPath(
        primaryButton,
        body,
      ),
      value,
      variables:
        getTemplateVariables(value),
      kind: 'button',
      label: 'Texto del botón',
    });
  }

  return fields.slice(
    0,
    EMAIL_MAX_SIMPLE_FIELDS,
  );
};

const escapeSimpleHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatEditableParagraph = (
  value,
) => {
  const escaped =
    escapeSimpleHtml(value);

  return escaped
    .replace(
      /\{\{\s*[A-Za-z0-9_]+\s*\}\}/g,
      (token) => `<strong>${token}</strong>`,
    )
    .replace(/\n/g, '<br>');
};

const updateVisibleTextBlock = (
  htmlContent,
  block,
  nextValue,
) => {
  const parser = new DOMParser();

  const documentNode = parser.parseFromString(
    String(htmlContent || ''),
    'text/html',
  );

  const target = findElementByPath(
    documentNode.body,
    block.path,
  );

  if (!target) {
    return htmlContent;
  }

  if (block.kind === 'paragraph') {
    target.innerHTML =
      formatEditableParagraph(nextValue);
  } else {
    target.textContent = nextValue;
  }

  return `<!DOCTYPE html>\n${documentNode.documentElement.outerHTML}`;
};

const findEmailHeaderCell = (
  documentNode,
) => {
  const logo = documentNode
    .querySelector(
      'img[src*="{{url_logo}}"]',
    );

  return logo?.closest('td') || null;
};

const readPaddingValues = (
  value,
) => {
  const numbers = String(value || '')
    .match(/\d+(?:\.\d+)?/g)
    ?.map(Number) || [];

  if (!numbers.length) {
    return {
      top: 28,
      bottom: 20,
    };
  }

  if (numbers.length === 1) {
    return {
      top: numbers[0],
      bottom: numbers[0],
    };
  }

  if (numbers.length === 2) {
    return {
      top: numbers[0],
      bottom: numbers[0],
    };
  }

  return {
    top: numbers[0],
    bottom: numbers[2],
  };
};

const getEmailHeaderSpacing = (
  htmlContent,
) => {
  if (
    !htmlContent
    || typeof DOMParser === 'undefined'
  ) {
    return 'normal';
  }

  const parser = new DOMParser();

  const documentNode = parser.parseFromString(
    String(htmlContent),
    'text/html',
  );

  const headerCell =
    findEmailHeaderCell(documentNode);

  if (!headerCell) {
    return 'normal';
  }

  const padding = readPaddingValues(
    headerCell.style.padding,
  );

  if (padding.top <= 18) {
    return 'compact';
  }

  if (padding.top >= 34) {
    return 'roomy';
  }

  return 'normal';
};

const updateEmailHeaderSpacing = (
  htmlContent,
  mode,
) => {
  const parser = new DOMParser();

  const documentNode = parser.parseFromString(
    String(htmlContent || ''),
    'text/html',
  );

  const headerCell =
    findEmailHeaderCell(documentNode);

  if (!headerCell) {
    return htmlContent;
  }

  const spacing =
    EMAIL_HEADER_SPACING[mode]
    || EMAIL_HEADER_SPACING.normal;

  headerCell.style.paddingTop =
    `${spacing.top}px`;

  headerCell.style.paddingBottom =
    `${spacing.bottom}px`;

  return `<!DOCTYPE html>\n${documentNode.documentElement.outerHTML}`;
};

const templateStatus = (template) => {
  if (!template.active) return { label: 'Inactiva', tone: 'yellow' };
  if (!template.connected) return { label: 'Preparada', tone: 'purple' };
  return { label: 'Conectada', tone: 'green' };
};

export default function AdminEmailCenter({ onToast }) {
  const [templates, setTemplates] = useState([]);
  const [branding, setBranding] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState('templates');
  const [editorMode, setEditorMode] = useState('simple');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [editorDensity, setEditorDensity] = useState(
    () => window.localStorage.getItem('provi-email-editor-density') || 'compact',
  );
  const [showGuides, setShowGuides] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState('');
  const htmlRef = useRef(null);

  const toast = useCallback((message) => {
    if (onToast) onToast(message);
  }, [onToast]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [templateResponse, brandingResponse] = await Promise.all([
        axios.get(`${API}/admin/email-center/templates`, { withCredentials: true }),
        axios.get(`${API}/admin/email-center/branding`, { withCredentials: true }),
      ]);
      const items = templateResponse.data?.items || [];
      setTemplates(items);
      setBranding(brandingResponse.data || {});
      if (!selectedType && items.length) setSelectedType(items[0].type);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'No se pudo cargar el Centro de correos.');
    } finally {
      setLoading(false);
    }
  }, [selectedType]);

  const loadDetail = useCallback(async (templateType) => {
    if (!templateType) return;
    setDetailLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${API}/admin/email-center/templates/${templateType}`,
        { withCredentials: true },
      );
      setDraft(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'No se pudo abrir la plantilla.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDetail(selectedType); }, [selectedType, loadDetail]);
  useEffect(() => {
    window.localStorage.setItem(
      'provi-email-editor-density',
      editorDensity,
    );
  }, [editorDensity]);

  const previewValues = useMemo(() => {
    const logo = draft?.logo_url
      || branding?.email_logo_url
      || `${window.location.origin}/email/provi-logo.png`;
    return {
      ...SAMPLE_VALUES,
      empresa: branding?.company_name || 'PROVI',
      company_name: branding?.company_name || 'PROVI',
      url_logo: draft?.logo_enabled === false || branding?.email_logo_enabled === false ? '' : logo,
      ancho_logo: draft?.logo_width || branding?.email_logo_width || 120,
      ubicacion_empresa: branding?.email_location || SAMPLE_VALUES.ubicacion_empresa,
      url_terminos: branding?.terms_url || SAMPLE_VALUES.url_terminos,
      url_privacidad: branding?.privacy_url || SAMPLE_VALUES.url_privacidad,
      url_soporte: branding?.support_url || SAMPLE_VALUES.url_soporte,
      correo_privacidad: branding?.privacy_email || SAMPLE_VALUES.correo_privacidad,
    };
  }, [branding, draft]);

  const previewHtml = useMemo(
    () => replaceVariables(draft?.html_content || '', previewValues),
    [draft?.html_content, previewValues],
  );

  const visibleTextBlocks = useMemo(
    () => extractVisibleTextBlocks(draft?.html_content || ''),
    [draft?.html_content],
  );

  const headerSpacing = useMemo(
    () => getEmailHeaderSpacing(draft?.html_content || ''),
    [draft?.html_content],
  );

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const updateVisibleText = (block, value) => {
    setDraft((current) => ({
      ...current,
      html_content: updateVisibleTextBlock(current?.html_content || '', block, value),
    }));
  };

  const updateHeaderSpacing = (mode) => {
    setDraft((current) => ({
      ...current,
      html_content: updateEmailHeaderSpacing(
        current?.html_content || '',
        mode,
      ),
    }));
  };

  const saveTemplate = async () => {
    if (!draft || saving) return;
    setSaving(true);
    try {
      const payload = {
        subject: draft.subject,
        html_content: draft.html_content,
        text_content: draft.text_content,
        active: Boolean(draft.active),
        logo_enabled: draft.logo_enabled !== false,
        logo_url: draft.logo_url || null,
        logo_width: draft.logo_width == null ? null : Number(draft.logo_width),
      };
      const response = await axios.put(
        `${API}/admin/email-center/templates/${draft.type}`,
        payload,
        { withCredentials: true },
      );
      setDraft(response.data);
      setTemplates((current) => current.map(
        (item) => item.type === response.data.type
          ? { ...item, ...response.data, html_content: undefined, text_content: undefined }
          : item,
      ));
      toast('Plantilla guardada y registrada en auditoría.');
    } catch (requestError) {
      toast(requestError.response?.data?.detail || 'No se pudo guardar la plantilla.');
    } finally {
      setSaving(false);
    }
  };

  const restoreTemplate = async () => {
    if (!draft || saving) return;
    const confirmed = window.confirm('Se restaurará el diseño oficial de esta plantilla. La versión anterior quedará respaldada.');
    if (!confirmed) return;
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/email-center/templates/${draft.type}/restore`,
        {},
        { withCredentials: true },
      );
      setDraft(response.data);
      toast('Diseño oficial restaurado.');
      await load();
    } catch (requestError) {
      toast(requestError.response?.data?.detail || 'No se pudo restaurar la plantilla.');
    } finally {
      setSaving(false);
    }
  };

  const loadVersions = async () => {
    if (!draft || historyLoading) return;
    setHistoryLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/email-center/templates/${draft.type}/versions`,
        { withCredentials: true },
      );
      setVersions(response.data?.items || []);
      setHistoryOpen(true);
    } catch (requestError) {
      toast(requestError.response?.data?.detail || 'No se pudo cargar el historial.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const restoreVersion = async (versionId) => {
    if (!draft || saving) return;
    const confirmed = window.confirm('Se restaurará esta versión. El contenido actual quedará respaldado.');
    if (!confirmed) return;
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/email-center/templates/${draft.type}/versions/${versionId}/restore`,
        {},
        { withCredentials: true },
      );
      setDraft(response.data);
      setHistoryOpen(false);
      toast('Versión restaurada correctamente.');
      await load();
    } catch (requestError) {
      toast(requestError.response?.data?.detail || 'No se pudo restaurar la versión.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = (checked) => {
    if (!checked) {
      const confirmed = window.confirm('Al desactivar esta plantilla, los correos automáticos ligados a esta acción dejarán de enviarse.');
      if (!confirmed) return;
    }
    updateDraft('active', checked);
  };

  const sendTest = async () => {
    if (!draft || sending || !testEmail.trim()) {
      toast('Escribe el correo que recibirá la prueba.');
      return;
    }
    if (!draft.active) {
      toast('Activa y guarda la plantilla antes de enviar una prueba.');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/email-center/templates/${draft.type}/test`,
        { to_email: testEmail.trim() },
        { withCredentials: true },
      );
      toast('Correo de prueba enviado.');
    } catch (requestError) {
      toast(requestError.response?.data?.detail || 'No se pudo enviar la prueba.');
    } finally {
      setSending(false);
    }
  };

  const saveBranding = async () => {
    if (!branding || saving) return;
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/email-center/branding`,
        {
          email_logo_url: branding.email_logo_url || null,
          email_logo_enabled: branding.email_logo_enabled !== false,
          email_logo_width: Number(branding.email_logo_width || 120),
          email_location: branding.email_location || 'Santa Rosa, El Oro, Ecuador',
          terms_url: branding.terms_url || 'https://proviapp.com/terminos',
          privacy_url: branding.privacy_url || 'https://proviapp.com/privacidad',
          support_url: branding.support_url || 'https://proviapp.com/soporte',
          privacy_email: branding.privacy_email || 'soporte@proviapp.com',
        },
        { withCredentials: true },
      );
      setBranding((current) => ({ ...current, ...response.data }));
      toast('Marca de correos guardada.');
    } catch (requestError) {
      toast(requestError.response?.data?.detail || 'No se pudo guardar la marca.');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    setLogoUploading(true);
    try {
      const response = await axios.post(
        `${API}/admin/email-center/logo`,
        body,
        { withCredentials: true },
      );
      setBranding((current) => ({
        ...current,
        email_logo_url: response.data.url,
        email_logo_enabled: true,
      }));
      toast('Logo subido. Guarda la marca para confirmar el resto de ajustes.');
    } catch (requestError) {
      toast(requestError.response?.data?.detail || 'No se pudo subir el logo.');
    } finally {
      setLogoUploading(false);
    }
  };

  const insertVariable = (variable) => {
    if (!draft) return;
    const token = `{{${variable}}}`;
    if (editorMode === 'subject') {
      updateDraft('subject', `${draft.subject || ''}${token}`);
      return;
    }
    const textarea = htmlRef.current;
    if (!textarea) {
      updateDraft('html_content', `${draft.html_content || ''}${token}`);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const next = `${draft.html_content.slice(0, start)}${token}${draft.html_content.slice(end)}`;
    updateDraft('html_content', next);
    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + token.length, start + token.length);
    });
  };

  if (loading) {
    return <div className="provi-email-center-state"><Loader2 className="spin"/>Cargando Centro de correos...</div>;
  }

  if (error && !templates.length) {
    return <div className="provi-email-center-state error"><XCircle/>{error}<button className="provi-btn" onClick={load}>Reintentar</button></div>;
  }

  return (
    <div className="provi-email-center">
      <div className="provi-email-center-tabs">
        <button className={tab === 'templates' ? 'active' : ''} onClick={() => setTab('templates')}><Mail/>Plantillas</button>
        <button className={tab === 'branding' ? 'active' : ''} onClick={() => setTab('branding')}><ImageIcon/>Logo y pie</button>
        <button className="refresh" onClick={load}><RefreshCw/>Actualizar</button>
      </div>
      {error && <div className="provi-email-inline-error"><XCircle/>{error}</div>}

      {tab === 'branding' ? (
        <section className="provi-email-branding">
          <div className="provi-email-brand-preview">
            <div className="logo-frame">
              {branding?.email_logo_enabled !== false && branding?.email_logo_url
                ? <img src={branding.email_logo_url} alt="Logo de correos PROVI" style={{ width: Number(branding.email_logo_width || 120) }}/>
                : <div className="logo-placeholder">P</div>}
            </div>
            <strong>Logo usado en correos</strong>
            <span>Se mostrará con fondo transparente y mediante URL HTTPS.</span>
          </div>
          <div className="provi-email-form-grid">
            <label className="provi-field full"><span>URL del logo</span><input className="provi-input" value={branding?.email_logo_url || ''} onChange={(event) => setBranding((current) => ({ ...current, email_logo_url: event.target.value }))}/></label>
            <label className="provi-field"><span>Ancho del logo: {branding?.email_logo_width || 120}px</span><input type="range" min="40" max="300" value={branding?.email_logo_width || 120} onChange={(event) => setBranding((current) => ({ ...current, email_logo_width: Number(event.target.value) }))}/></label>
            <label className="provi-email-switch"><input type="checkbox" checked={branding?.email_logo_enabled !== false} onChange={(event) => setBranding((current) => ({ ...current, email_logo_enabled: event.target.checked }))}/><span>Mostrar logo</span></label>
            <label className="provi-field"><span>Ubicación del pie</span><input className="provi-input" value={branding?.email_location || ''} onChange={(event) => setBranding((current) => ({ ...current, email_location: event.target.value }))}/></label>
            <label className="provi-field"><span>Correo de privacidad</span><input className="provi-input" value={branding?.privacy_email || ''} onChange={(event) => setBranding((current) => ({ ...current, privacy_email: event.target.value }))}/></label>
            <label className="provi-field"><span>URL de términos</span><input className="provi-input" value={branding?.terms_url || ''} onChange={(event) => setBranding((current) => ({ ...current, terms_url: event.target.value }))}/></label>
            <label className="provi-field"><span>URL de privacidad</span><input className="provi-input" value={branding?.privacy_url || ''} onChange={(event) => setBranding((current) => ({ ...current, privacy_url: event.target.value }))}/></label>
            <label className="provi-field full"><span>URL de soporte</span><input className="provi-input" value={branding?.support_url || ''} onChange={(event) => setBranding((current) => ({ ...current, support_url: event.target.value }))}/></label>
          </div>
          <div className="provi-email-actions">
            <label className="provi-btn"><Upload/>{logoUploading ? 'Subiendo...' : 'Subir logo'}<input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={uploadLogo} disabled={logoUploading}/></label>
            <button className="provi-btn primary" onClick={saveBranding} disabled={saving}><Save/>{saving ? 'Guardando...' : 'Guardar marca'}</button>
          </div>
        </section>
      ) : (
        <div className="provi-email-layout">
          <aside className="provi-email-template-list">
            {templates.map((template) => {
              const statusInfo = templateStatus(template);
              return (
                <button key={template.type} className={selectedType === template.type ? 'active' : ''} onClick={() => setSelectedType(template.type)}>
                  <span className="template-icon"><Mail/></span>
                  <span><strong>{template.name}</strong><small>{template.subject}</small></span>
                  <span className={`provi-email-badge ${statusInfo.tone}`}>{statusInfo.label}</span>
                </button>
              );
            })}
          </aside>

          <section className="provi-email-editor">
            {detailLoading || !draft ? (
              <div className="provi-email-center-state"><Loader2 className="spin"/>Abriendo plantilla...</div>
            ) : (
              <>
                <header>
                  <div>
                    <h3>{draft.name}</h3>
                    <p>Acción: <code>{draft.type}</code></p>
                  </div>
                  <label className="provi-email-switch"><input type="checkbox" checked={Boolean(draft.active)} onChange={(event) => toggleActive(event.target.checked)}/><span>Activa</span></label>
                </header>

                {!draft.connected && (
                  <div className="provi-email-warning">
                    <Settings2/>
                    <span>Esta plantilla ya se puede editar y probar, pero todavía no está ligada a una acción automática.</span>
                  </div>
                )}

                <div className="provi-email-editor-tabs">
                  <button className={editorMode === 'simple' ? 'active' : ''} onClick={() => setEditorMode('simple')}><FileText/>Contenido</button>
                  <button className={editorMode === 'html' ? 'active' : ''} onClick={() => setEditorMode('html')}><Code2/>HTML avanzado</button>
                  <button className={editorMode === 'preview' ? 'active' : ''} onClick={() => setEditorMode('preview')}><Eye/>Vista previa</button>
                </div>

                {editorMode === 'simple' && (
                  <div className={`provi-email-simple-editor ${editorDensity}`}>
                    <label className="provi-field full"><span>Asunto del correo</span><input className="provi-input" value={draft.subject || ''} onChange={(event) => updateDraft('subject', event.target.value)}/></label>

                    <section className="provi-email-layout-controls">
                      <div className="provi-email-control-info">
                        <strong>Altura del encabezado</strong>
                        <span>Reduce o amplía el espacio alrededor del logo y la marca.</span>
                      </div>
                      <div className="provi-email-segmented" role="group" aria-label="Altura del encabezado">
                        <button type="button" className={headerSpacing === 'compact' ? 'active' : ''} onClick={() => updateHeaderSpacing('compact')}>Compacta</button>
                        <button type="button" className={headerSpacing === 'normal' ? 'active' : ''} onClick={() => updateHeaderSpacing('normal')}>Normal</button>
                        <button type="button" className={headerSpacing === 'roomy' ? 'active' : ''} onClick={() => updateHeaderSpacing('roomy')}>Amplia</button>
                      </div>
                      <div className="provi-email-control-info">
                        <strong>Densidad del editor</strong>
                        <span>Cambia solo la altura de los campos administrativos.</span>
                      </div>
                      <div className="provi-email-segmented" role="group" aria-label="Densidad del editor">
                        <button type="button" className={editorDensity === 'compact' ? 'active' : ''} onClick={() => setEditorDensity('compact')}>Compacto</button>
                        <button type="button" className={editorDensity === 'normal' ? 'active' : ''} onClick={() => setEditorDensity('normal')}>Normal</button>
                      </div>
                    </section>

                    <section className="provi-email-copy-editor">
                      <header>
                        <div>
                          <strong>Textos visibles del correo</strong>
                          <span>Edita entre 2 y 4 textos principales. La marca, los datos automáticos y el pie legal permanecen protegidos.</span>
                        </div>
                        <b>{visibleTextBlocks.length} campos principales</b>
                      </header>

                      <div className="provi-email-copy-list">
                        {visibleTextBlocks.map((block, index) => (
                          <label className="provi-email-copy-field" key={`${block.id}-${index}`}>
                            <span>
                              {block.label}
                              {block.variables.length > 0 && (
                                <small>Dato automático protegido: {block.variables.map((item) => `{{${item}}}`).join(', ')}</small>
                              )}
                            </span>
                            <textarea
                              value={block.value}
                              onChange={(event) => updateVisibleText(block, event.target.value)}
                              rows={
                                editorDensity === 'compact'
                                  ? block.kind === 'paragraph' ? 3 : 2
                                  : block.kind === 'paragraph' ? 5 : 3
                              }
                            />
                          </label>
                        ))}
                      </div>
                    </section>

                    <details className="provi-email-plain-text">
                      <summary>Versión de texto simple</summary>
                      <p>Se usa cuando el correo del destinatario no permite diseño HTML.</p>
                      <textarea className="provi-textarea" value={draft.text_content || ''} onChange={(event) => updateDraft('text_content', event.target.value)}/>
                    </details>

                    <div className="provi-email-logo-options">
                      <label className="provi-email-switch"><input type="checkbox" checked={draft.logo_enabled !== false} onChange={(event) => updateDraft('logo_enabled', event.target.checked)}/><span>Mostrar logo en esta plantilla</span></label>
                      <label className="provi-field"><span>Logo particular, opcional</span><input className="provi-input" placeholder="Vacío = usar logo global" value={draft.logo_url || ''} onChange={(event) => updateDraft('logo_url', event.target.value)}/></label>
                      <label className="provi-email-switch">
                        <input
                          type="checkbox"
                          checked={draft.logo_width == null}
                          onChange={(event) => updateDraft(
                            'logo_width',
                            event.target.checked ? null : Number(branding?.email_logo_width || 120),
                          )}
                        />
                        <span>Usar tamaño global ({branding?.email_logo_width || 120}px)</span>
                      </label>
                      {draft.logo_width != null && (
                        <label className="provi-field">
                          <span>Tamaño particular: {draft.logo_width}px</span>
                          <input type="range" min="40" max="300" value={draft.logo_width} onChange={(event) => updateDraft('logo_width', Number(event.target.value))}/>
                        </label>
                      )}
                    </div>
                  </div>
                )}

                {editorMode === 'html' && (
                  <div className="provi-email-code-editor">
                    <div className="provi-email-variable-bar">
                      <span>Insertar dato:</span>
                      {(draft.available_variables || []).map((variable) => <button key={variable} onClick={() => insertVariable(variable)}>{`{{${variable}}}`}</button>)}
                    </div>
                    <textarea ref={htmlRef} spellCheck="false" value={draft.html_content || ''} onChange={(event) => updateDraft('html_content', event.target.value)}/>
                    <small>El sistema bloquea scripts, formularios, iframes y variables desconocidas.</small>
                  </div>
                )}

                {editorMode === 'preview' && (
                  <div className="provi-email-preview-panel">
                    <div className="provi-email-preview-tools">
                      <button className={previewDevice === 'desktop' ? 'active' : ''} onClick={() => setPreviewDevice('desktop')}>Escritorio</button>
                      <button className={previewDevice === 'mobile' ? 'active' : ''} onClick={() => setPreviewDevice('mobile')}>Móvil</button>
                      <button className={showGuides ? 'active' : ''} onClick={() => setShowGuides((current) => !current)}>Mostrar guías</button>
                    </div>
                    <div className={`provi-email-preview-frame ${previewDevice} ${showGuides ? 'guides' : ''}`}>
                      <iframe title={`Vista previa ${draft.name}`} sandbox="" srcDoc={previewHtml}/>
                      <div className="provi-email-guide-overlay" aria-hidden="true">
                        <span>Área útil 600 px</span>
                      </div>
                    </div>
                  </div>
                )}

                <footer className="provi-email-editor-footer">
                  <div className="provi-email-test">
                    <input className="provi-input" type="email" placeholder="correo@ejemplo.com" value={testEmail} onChange={(event) => setTestEmail(event.target.value)}/>
                    <button className="provi-btn" onClick={sendTest} disabled={sending}><Send/>{sending ? 'Enviando...' : 'Enviar prueba'}</button>
                  </div>
                  <div className="provi-email-actions">
                    <button className="provi-btn" onClick={loadVersions} disabled={historyLoading}><History/>{historyLoading ? 'Cargando...' : 'Historial'}</button>
                    <button className="provi-btn" onClick={restoreTemplate} disabled={saving}><RotateCcw/>Restaurar oficial</button>
                    <button className="provi-btn primary" onClick={saveTemplate} disabled={saving}><Save/>{saving ? 'Guardando...' : 'Guardar plantilla'}</button>
                  </div>
                </footer>
              </>
            )}
          </section>
        </div>
      )}
      {historyOpen && (
        <div className="provi-email-history-backdrop" onClick={(event) => { if (event.target === event.currentTarget) setHistoryOpen(false); }}>
          <section className="provi-email-history" role="dialog" aria-modal="true">
            <header><div><h3>Historial de {draft?.name}</h3><p>Las versiones anteriores no afectan datos de usuarios.</p></div><button className="provi-btn" onClick={() => setHistoryOpen(false)}>Cerrar</button></header>
            {versions.length ? (
              <div className="provi-email-history-list">
                {versions.map((version) => (
                  <article key={version.id}>
                    <div><strong>{version.action === 'manual_update' ? 'Edición manual' : version.action === 'restore_official' ? 'Restauración oficial' : version.action === 'restore_version' ? 'Restauración de versión' : 'Actualización del sistema'}</strong><span>{version.created_at ? new Date(version.created_at).toLocaleString('es-EC') : 'Fecha no disponible'} · {version.actor_email || 'Sistema'}</span></div>
                    <button className="provi-btn" onClick={() => restoreVersion(version.id)} disabled={saving}>Restaurar</button>
                  </article>
                ))}
              </div>
            ) : <div className="provi-email-center-state">Todavía no hay versiones anteriores.</div>}
          </section>
        </div>
      )}
    </div>
  );
}
